import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { TZDate } from '@date-fns/tz'
import { Users } from 'lucide-react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { FilterField } from '@/components/FilterButton'
import { usePersistedState } from '@/hooks/usePersistedState'
import { StatusFilterButton } from '@/components/StatusFilter'
import { deliveryStatusOf } from '@/components/DeliveryStatusBadge'
import { AdvertiserSelect } from '@/components/AdvertiserSelect'
import { OrderSelect } from '@/components/OrderSelect'
import { SearchSelect } from '@/components/SearchSelect'
import { DateTimePicker } from '@/components/DateTimePicker'
import { ALL_COUNTRIES, CountryFilter } from '@/components/CountryFilter'
import { useNetwork } from '@/contexts/NetworkContext'
import {
  DeliveryStatusFilter,
  LINE_ITEM_SOURCES,
  LineItem,
  type LineItemDateBound,
  type LineItemFetchFilters,
  type LineItemSource,
} from '@/services/lineItems/lineItemsApi'
import { TeamsApi } from '@/services/users/teamsApi'
import { hiddenTargetingTypes, TARGETING_TYPES } from '@/services/targeting/targeting'

// ─── Types ─────────────────────────────────────────────────────────────────

export type ObjectStateFilter  = 'all' | 'ACTIVE' | 'INACTIVE'
export type DeliveryStateFilter = DeliveryStatusFilter

/** A calendar day in the network's zone, and which side of it to keep. */
export interface DayFilter {
  op:  '>=' | '<='
  /** `yyyy-MM-dd`, or '' when the date is not filtered on. */
  day: string
}

export interface LineItemFilterState {
  objectState:    ObjectStateFilter
  deliveryState:  DeliveryStateFilter
  /** Advertiser id the list is narrowed to, or '' for every advertiser. */
  advertiserId:   string
  /**
   * Country code of the team owning the line item's order, or '' for every
   * country. The API only applies it when no advertiser is picked.
   */
  teamCountry:    string
  /** Order id, or '' for every order. */
  orderId:        string
  /** Id of the team owning the line item's order, or '' for every team. */
  teamId:         string
  /** The backend `TargetType` the line item must target by, or '' for any. */
  targetType:     string
  source:         LineItemSource | ''
  startDate:      DayFilter
  endDate:        DayFilter
}

const DEFAULT_FILTERS: LineItemFilterState = {
  objectState:   'ACTIVE',
  deliveryState: 'all',
  advertiserId:  '',
  teamCountry:   '',
  orderId:       '',
  teamId:        '',
  targetType:    '',
  source:        '',
  startDate:     { op: '>=', day: '' },
  endDate:       { op: '<=', day: '' },
}

/** The day filter as the API takes it, or nothing when no day is picked. */
function dayBound(filter: DayFilter): LineItemDateBound | undefined {
  return filter.day ? filter : undefined
}

/**
 * Whether a line item date lies on the kept side of a day, read in the
 * network's zone as the API does. Like the API, a missing end date counts as
 * after any day, and a missing start date as before any.
 */
function withinDay(
  value: string | null | undefined,
  { op, day }: LineItemDateBound,
  field: 'startDate' | 'endDate',
  timeZone: string
): boolean {
  if (!value) return field === 'endDate' ? op === '>=' : op === '<='
  const [year, month, date] = day.split('-').map(Number)
  const time = new Date(value).getTime()
  return op === '>='
    ? time >= new TZDate(year, month - 1, date, timeZone).getTime()
    : time < new TZDate(year, month - 1, date + 1, timeZone).getTime()
}

// ─── Hook ──────────────────────────────────────────────────────────────────

/**
 * @param initial  the page's own starting point — what "unfiltered" means there.
 * @param storageKey  name the list is remembered under; lists sharing a key
 *                    share their filters. Defaults to the main line item list.
 */
export function useLineItemFilters(
  initial?: Partial<LineItemFilterState>,
  storageKey = 'lineItems',
) {
  const { currentNetwork } = useNetwork()
  const timeZone = currentNetwork?.timeZone ?? 'UTC'

  // The page's own starting point is what "unfiltered" means for that page,
  // so an embedded list starting on 'all' does not look filtered.
  const [defaults] = useState<LineItemFilterState>({ ...DEFAULT_FILTERS, ...initial })
  const [stored, setFilters] = usePersistedState<LineItemFilterState>(`filters.${storageKey}`, defaults)

  // A remembered value predates any filter added since it was stored, so the
  // defaults fill in what it does not carry.
  const filters: LineItemFilterState = { ...defaults, ...stored }

  const startBound = dayBound(filters.startDate)
  const endBound = dayBound(filters.endDate)

  /** The filters as the line item API takes them. */
  const fetchFilters: LineItemFetchFilters = {
    objectState:   filters.objectState,
    deliveryState: filters.deliveryState,
    advertiserId:  filters.advertiserId,
    teamCountry:   filters.teamCountry,
    orderId:       filters.orderId,
    teamId:        filters.teamId,
    targetType:    filters.targetType,
    source:        filters.source || undefined,
    startDate:     startBound,
    endDate:       endBound,
  }

  /** Filters a list fetched whole. Order, team, targeting and source need the API. */
  function applyFilters(items: LineItem[]): LineItem[] {
    return items.filter((li) => {
      if (filters.objectState  !== 'all' && li.objectState  !== filters.objectState)  return false
      if (filters.deliveryState !== 'all' && deliveryStatusOf(li) !== filters.deliveryState) return false
      if (filters.advertiserId && li.advertiserId !== filters.advertiserId) return false
      if (startBound && !withinDay(li.startDate, startBound, 'startDate', timeZone)) return false
      if (endBound && !withinDay(li.endDate, endBound, 'endDate', timeZone)) return false
      return true
    })
  }

  return { filters, setFilters, applyFilters, fetchFilters, defaults }
}

// ─── Filter button ─────────────────────────────────────────────────────────

/** The delivery statuses the API can filter on, in the order they are offered. */
const DELIVERY_OPTIONS: DeliveryStateFilter[] = [
  'all', 'RUNNING', 'READY', 'PAUSED', 'NOT_READY', 'PROPOSED', 'STOPPED', 'ENDED',
]

/** Stands in for "any" in selects — Radix items need a non-empty value. */
const ANY = '__any__'

interface LineItemFiltersProps {
  filters: LineItemFilterState
  onChange: (f: LineItemFilterState) => void
  /** What the page treats as unfiltered — from `useLineItemFilters`. */
  defaults: LineItemFilterState
  /** Off for lists already scoped to one advertiser, where it says nothing. */
  showAdvertiser?: boolean
  /** On for lists fetched across advertisers, where the team country applies. */
  showCountry?: boolean
  /**
   * On for lists the API filters page by page, which alone can narrow by
   * order, team, targeting and source.
   */
  serverFiltered?: boolean
}

/** Status, delivery, relation and date filters for a line item list, in one button. */
export function LineItemFilters({
  filters,
  onChange,
  defaults,
  showAdvertiser = true,
  showCountry = false,
  serverFiltered = false,
}: LineItemFiltersProps) {
  const { t } = useTranslation()
  const { scopeKey, currentNetwork } = useNetwork()

  // The countries on offer are those the network's teams are in.
  const { data: teams = [] } = useQuery({
    queryKey: ['teams', 'listFilter', scopeKey],
    queryFn: () => TeamsApi.getAll(),
    enabled: showCountry || serverFiltered,
    staleTime: 5 * 60 * 1000,
  })
  const countryCodes = useMemo(
    () => [...new Set(teams.map((team) => team.country).filter((c): c is string => !!c))],
    [teams]
  )
  const teamOptions = useMemo(
    () => teams.map((team) => ({ value: team.id, label: team.name })),
    [teams]
  )

  // A single choice has nothing to narrow, but a remembered pick stays
  // visible so it can be cleared.
  const countryVisible = showCountry && (countryCodes.length > 1 || !!filters.teamCountry)
  const teamVisible = serverFiltered && (teams.length > 1 || !!filters.teamId)
  // Only self-service and marketplace networks have line items from more than one source.
  const sourceVisible =
    serverFiltered &&
    (!!currentNetwork?.suiPlatform || !!currentNetwork?.marketplacePlatform || !!filters.source)

  const hiddenTypes = hiddenTargetingTypes(currentNetwork)
  const targetingOptions = TARGETING_TYPES.filter((type) => !hiddenTypes.includes(type.id))
  const targetingLabel = (apiType: string) => {
    const type = TARGETING_TYPES.find((tt) => tt.apiType === apiType)
    return type ? t(`targeting.types.${type.id}.plural`) : apiType
  }

  const label = (value: DeliveryStateFilter) =>
    value === 'all' ? t('common.all') : t(`deliveryStatuses.${value}`)

  type IdField = 'advertiserId' | 'orderId' | 'teamId' | 'teamCountry' | 'targetType' | 'source'
  const changed = (visible: boolean, key: IdField) =>
    visible && filters[key] !== defaults[key] ? 1 : 0

  // The API honours one of order, advertiser and team (or team country) — in
  // that order — so picking one clears the ones it would silently override or
  // be overridden by. An order stays within the picked advertiser.
  const setAdvertiser = (advertiserId: string) =>
    onChange({
      ...filters,
      advertiserId,
      orderId: '',
      teamId: advertiserId ? '' : filters.teamId,
      teamCountry: advertiserId ? '' : filters.teamCountry,
    })
  const setOrder = (orderId: string) =>
    onChange({
      ...filters,
      orderId,
      teamId: orderId ? '' : filters.teamId,
      teamCountry: orderId ? '' : filters.teamCountry,
    })
  const setTeam = (teamId: string) =>
    onChange({
      ...filters,
      teamId,
      advertiserId: teamId ? '' : filters.advertiserId,
      orderId: teamId ? '' : filters.orderId,
      teamCountry: teamId ? '' : filters.teamCountry,
    })
  const setCountry = (teamCountry: string) =>
    onChange({
      ...filters,
      teamCountry,
      advertiserId: teamCountry ? '' : filters.advertiserId,
      orderId: teamCountry ? '' : filters.orderId,
      teamId: teamCountry ? '' : filters.teamId,
    })

  const dayField = (key: 'startDate' | 'endDate') => {
    const value = filters[key]
    return (
      <FilterField label={t(`common.${key}`)}>
        <Select
          value={value.op}
          onValueChange={(op) =>
            onChange({ ...filters, [key]: { ...value, op: op as DayFilter['op'] } })
          }
        >
          <SelectTrigger className="h-8 w-full text-sm">
            <SelectValue>
              {value.op === '>=' ? t('lineItems.onOrAfter') : t('lineItems.onOrBefore')}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value=">=">{t('lineItems.onOrAfter')}</SelectItem>
            <SelectItem value="<=">{t('lineItems.onOrBefore')}</SelectItem>
          </SelectContent>
        </Select>
        <DateTimePicker
          value={value.day}
          onChange={(day) => onChange({ ...filters, [key]: { ...value, day } })}
          placeholder={t('common.all')}
          dateOnly
        />
      </FilterField>
    )
  }

  return (
    <StatusFilterButton
      value={filters.objectState}
      onChange={(v) => onChange({ ...filters, objectState: v })}
      defaultValue={defaults.objectState}
      extraActiveCount={
        (filters.deliveryState !== defaults.deliveryState ? 1 : 0) +
        changed(showAdvertiser, 'advertiserId') +
        changed(countryVisible, 'teamCountry') +
        changed(serverFiltered, 'orderId') +
        changed(teamVisible, 'teamId') +
        changed(serverFiltered, 'targetType') +
        changed(sourceVisible, 'source') +
        (filters.startDate.day ? 1 : 0) +
        (filters.endDate.day ? 1 : 0)
      }
      onReset={() => onChange(defaults)}
    >
      <FilterField label={t('common.delivery')}>
        <Select
          value={filters.deliveryState}
          onValueChange={(v) => onChange({ ...filters, deliveryState: v as DeliveryStateFilter })}
        >
          <SelectTrigger className="h-8 w-full text-sm">
            <SelectValue>{label(filters.deliveryState)}</SelectValue>
          </SelectTrigger>
          <SelectContent>
            {DELIVERY_OPTIONS.map((value) => (
              <SelectItem key={value} value={value}>{label(value)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FilterField>

      {showAdvertiser && (
        <FilterField label={t('common.advertiser')}>
          <AdvertiserSelect
            value={filters.advertiserId}
            onChange={setAdvertiser}
            anyLabel={t('common.all')}
          />
        </FilterField>
      )}

      {serverFiltered && (
        <FilterField label={t('lineItems.order')}>
          <OrderSelect
            value={filters.orderId}
            onChange={setOrder}
            advertiserId={filters.advertiserId || undefined}
            anyLabel={t('common.all')}
            className="h-8 text-sm"
          />
        </FilterField>
      )}

      {teamVisible && (
        <FilterField label={t('common.team')}>
          <SearchSelect
            value={filters.teamId}
            onChange={setTeam}
            options={teamOptions}
            placeholder={t('common.all')}
            clearable
            icon={Users}
            searchPlaceholder={t('teams.searchTeams')}
            emptyText={t('teams.noTeamsFound')}
            className="h-8 text-sm"
          />
        </FilterField>
      )}

      {countryVisible && (
        <FilterField label={t('common.country')}>
          <CountryFilter
            value={filters.teamCountry || ALL_COUNTRIES}
            onChange={(v) => setCountry(v === ALL_COUNTRIES ? '' : v)}
            codes={countryCodes}
          />
        </FilterField>
      )}

      {serverFiltered && (
        <FilterField label={t('targeting.title')}>
          <Select
            value={filters.targetType || ANY}
            onValueChange={(v) => onChange({ ...filters, targetType: v === ANY ? '' : v })}
          >
            <SelectTrigger className="h-8 w-full text-sm">
              <SelectValue>
                {filters.targetType ? targetingLabel(filters.targetType) : t('common.all')}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>{t('common.all')}</SelectItem>
              {targetingOptions.map((type) => (
                <SelectItem key={type.apiType} value={type.apiType}>
                  {t(`targeting.types.${type.id}.plural`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FilterField>
      )}

      {sourceVisible && (
        <FilterField label={t('lineItems.source')}>
          <Select
            value={filters.source || ANY}
            onValueChange={(v) =>
              onChange({ ...filters, source: v === ANY ? '' : (v as LineItemSource) })
            }
          >
            <SelectTrigger className="h-8 w-full text-sm">
              <SelectValue>
                {filters.source ? t(`lineItems.sources.${filters.source}`) : t('common.all')}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>{t('common.all')}</SelectItem>
              {LINE_ITEM_SOURCES.map((source) => (
                <SelectItem key={source} value={source}>{t(`lineItems.sources.${source}`)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FilterField>
      )}

      {dayField('startDate')}
      {dayField('endDate')}
    </StatusFilterButton>
  )
}
